/** WOODWORKING FURNITURE PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume {
  number: string
  title: string
  projects: string
  description: string
  /** Real cover artwork, shown whole in the volume card. */
  cover: PlanImage
  /**
   * The ten project names from this volume's index, in order. Project codes
   * (P01–P60) are derived from the volume number and position.
   */
  projectList: string[]
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'rights'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Bonus {
  number: string
  title: string
  benefit: string
  cover: PlanImage
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/9147da2f-b8f0-4d09-8375-7ee02ea14e90/`

export const PRODUCT = {
  name: 'WOODWORKING FURNITURE PLANS™',
  descriptor: 'Furniture Build Plans for Woodworkers & Furniture Makers',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build for yourself—or sell the finished furniture you make.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '60 FURNITURE PROJECTS',
  '6 DIGITAL VOLUMES',
  'INCHES & MILLIMETERS',
  'COMMERCIAL BUILD RIGHTS',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages and covers share the same portrait page ratio. */
const PAGE_SIZE = { width: 1688, height: 2184 }

export const SNAPSHOT: SnapshotItem[] = [
  { label: '60 Furniture Projects', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Inches + Millimeters', icon: 'dimensions' },
  { label: 'Build & Sell Rights', icon: 'rights' },
]

export const PROJECT_NAME = 'Project 04 — Three-Drawer Document Cabinet'

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792696697491_landing-p04-01-finished-cabinet-27a51268.webp`,
    caption: 'Finished Cabinet',
    alt: 'Volume 01, page 26: Project 04 Three-Drawer Document Cabinet, finished oak cabinet with front-view dimensions in millimeters and inches',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696703365_landing-p04-02-dimensioned-plans-7e247d4d.webp`,
    caption: 'Dimensions & Technical Views',
    alt: 'Volume 01, page 27: dimensioned plans of the Three-Drawer Document Cabinet with front, side, top and isometric views',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696708871_landing-p04-03-exploded-view-9be67043.webp`,
    caption: 'Exploded View & Cut List',
    alt: 'Volume 01, page 28: exploded view of the cabinet with labeled parts and a cut list table with quantities, materials and dimensions',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696714581_landing-p04-04-wood-connections-e4bf07f3.webp`,
    caption: 'Wood Connections & Machining',
    alt: 'Volume 01, page 29: wood connection and machining details with screw sizes, clearance and pilot holes for each joint',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696720369_landing-p04-05-hardware-clearances-c9ecc5e7.webp`,
    caption: 'Hardware & Clearances',
    alt: 'Volume 01, page 30: drawer slide details, functional clearances, anti-tip restraint and hardware summary table',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696725773_landing-p04-06-assembly-structure-f56b37ac.webp`,
    caption: 'Assembly — Structure',
    alt: 'Volume 01, page 31: structure assembly steps — labeled parts layout, plinth frame and carcass assembly',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696731954_landing-p04-07-assembly-fittings-860fbd91.webp`,
    caption: 'Assembly — Fittings & Completion',
    alt: 'Volume 01, page 32: assembly steps for the carcass, drawer boxes, slides and fronts, and completion',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696737747_landing-p04-08-build-check-c8322fde.webp`,
    caption: 'Finished Reference & Build Check',
    alt: 'Volume 01, page 33: finished cabinet reference with overall dimensions, detail views and a build checklist',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = ['Plan your materials.', 'See how the parts fit.', 'Follow the build sequence.']

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792696761547_landing-tech-01-dimensions-08c212e9.webp`,
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 01 · Single-Pedestal Desk with Two Drawers · Volume 01',
    description: 'Review the dimensions and technical views before cutting.',
    alt: 'Volume 01, page 7: Project 01 Single-Pedestal Desk with Two Drawers, dimensioned front, side and top views in millimeters and inches',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696767113_landing-tech-02-cut-list-1430ca0a.webp`,
    title: 'KNOW WHAT TO CUT',
    project: 'Project 12 · Coffee Table with Lower Shelf · Volume 02',
    description: 'Identify the parts, quantities, materials, and finished cut sizes.',
    alt: 'Volume 02, page 11: Project 12 Coffee Table with Lower Shelf, exploded view with part IDs and finished cut list table',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696772924_landing-tech-03-joints-hardware-3329d5de.webp`,
    title: 'SEE HOW IT FITS',
    project: 'Project 11 · Three-Bay Media Console · Volume 02',
    description: 'Inspect the illustrated joint details and hardware information.',
    alt: 'Volume 02, page 6: Project 11 Three-Bay Media Console, joint and hardware details with screw specifications and cable aperture positions',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792696778816_landing-tech-04-build-sequence-06e12ae6.webp`,
    title: 'FOLLOW THE BUILD',
    project: 'Project 02 · Knock-Down L-Shaped Desk · Volume 01',
    description: 'Follow the illustrated sequence from prepared parts to assembled furniture.',
    alt: 'Volume 01, page 18: Project 02 Knock-Down L-Shaped Desk, six-step illustrated build sequence from prepared parts to finished desk',
    ...PAGE_SIZE,
  },
]

export const COVER_VOLUME_01: PlanImage = {
  src: `${IMG}1792696785362_landing-cover-volume-01-6ccd9f62.webp`,
  alt: 'Volume 01 cover: Woodworking Furniture Plans™ — Office & Workspace',
  ...PAGE_SIZE,
}

export const COVER_VOLUME_02: PlanImage = {
  src: `${IMG}1792696791369_landing-cover-volume-02-a348035b.webp`,
  alt: 'Volume 02 cover: Woodworking Furniture Plans™ — Living & Media',
  ...PAGE_SIZE,
}

/** Transparent product mockup used in the hero and the offer. */
export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792696797355_landing-mockup-hero-f6f3c738.webp`,
  alt: 'WOODWORKING FURNITURE PLANS™ digital collection: the complete-collection cover, a tablet showing the Project 04 dimensioned plans, a printed plan sheet, an example three-drawer cabinet built from the plans, and a "5 bonus resources included free" badge',
  width: 1254,
  height: 1254,
}

export const BONUS_LINE = 'Plus 5 bonus resources, included free.'

const cover = (src: string, number: string, title: string): PlanImage => ({
  src,
  alt: `Volume ${number} cover: Woodworking Furniture Plans™ — ${title}`,
  ...PAGE_SIZE,
})

export const VOLUMES: Volume[] = [
  {
    number: '01',
    title: 'Office & Workspace',
    projects: '10 PROJECTS',
    description: 'Desks, workstations, cabinets, and practical office storage.',
    projectList: [
      'Single-Pedestal Desk with Two Drawers',
      'Knock-Down L-Shaped Desk',
      'Two-Person Workstation',
      'Three-Drawer Document Cabinet',
      'Mobile Printer Cabinet',
      'Five-Shelf Bookcase',
      'Wall Cabinet with Two Doors',
      'Low Office Storage Credenza',
      'Wall-Folding Compact Desk',
      'Fixed-Height Standing Work Desk',
    ],
    cover: COVER_VOLUME_01,
  },
  {
    number: '02',
    title: 'Living & Media',
    projects: '10 PROJECTS',
    description: 'Media consoles, coffee tables, side tables, and display furniture.',
    projectList: [
      'Three-Bay Media Console',
      'Coffee Table with Lower Shelf',
      'Side Table with One Drawer',
      'Cantilever C Side Table',
      'Four-Door Sideboard',
      'Six-Cube Display Unit',
      'Record Player and Vinyl Cabinet',
      'Slim Media Console',
      'Nesting Side Table Pair',
      'Three-Level Wall Display Shelf',
    ],
    cover: COVER_VOLUME_02,
  },
  {
    number: '03',
    title: 'Bedroom & Personal Space',
    projects: '10 PROJECTS',
    description: 'Furniture projects for bedrooms and personal spaces.',
    projectList: [
      'Two-Drawer Nightstand',
      'Open Bedside Table',
      'Floating Bedside Cabinet',
      'Knock-Down Platform Bed',
      'Headboard with Bedside Ledges',
      'Dressing Table with Two Drawers',
      'End-of-Bed Storage Chest',
      'Rolling Under-Bed Storage Drawer',
      'Open Clothes Rack with Shoe Shelf',
      'Freestanding Full-Length Mirror Frame',
    ],
    cover: cover(`${IMG}1792696803100_landing-cover-volume-03-a3060b50.webp`, '03', 'Bedroom & Personal Space'),
  },
  {
    number: '04',
    title: 'Entryway & Organization',
    projects: '10 PROJECTS',
    description: 'Furniture for entryways, storage, and everyday organization.',
    projectList: [
      'Entry Console Table',
      'Open Three-Level Shoe Rack',
      'Two-Door Shoe Cabinet',
      'Freestanding Coat Tree',
      'Wall Coat Panel with Shelf',
      'Keys and Mail Wall Organizer',
      'Open Shoe Storage Bench',
      'Hall Tree with Cubbies and Hooks',
      'Umbrella Stand with Removable Tray',
      'Compact Lift-Seat Entry Bench',
    ],
    cover: cover(`${IMG}1792696826172_landing-cover-volume-04-5d292662.webp`, '04', 'Entryway & Organization'),
  },
  {
    number: '05',
    title: 'Kitchen, Dining & Utility',
    projects: '10 PROJECTS',
    description: 'Practical furniture for dining areas, kitchens, and utility spaces.',
    projectList: [
      'Freestanding Kitchen Island',
      'Buffet with Drawers and Cupboards',
      'Indoor Serving Cart',
      'Home Bar Cabinet with Bottle Grid',
      'Tall Pantry Cabinet',
      'Laundry Supply Cabinet',
      'Folding Ironing Board Storage Cabinet',
      'Mobile Materials Organizer',
      'Wall-Folding Breakfast Table',
      'Three-Tier Countertop Spice Organizer',
    ],
    cover: cover(`${IMG}1792696832109_landing-cover-volume-05-99e68530.webp`, '05', 'Kitchen, Dining & Utility'),
  },
  {
    number: '06',
    title: 'Patio, Garden & Outdoor',
    projects: '10 PROJECTS',
    description: 'Furniture projects for patios, gardens, and outdoor spaces.',
    projectList: [
      'Raised Planter Box',
      'Three-Tier Plant Stand',
      'Potting Bench with Lower Shelf',
      'Outdoor Serving Trolley',
      'Lattice Privacy Panel',
      'Garden Bench',
      'Patio Side Table',
      'Outdoor Storage Box',
      'Patio Dining Table',
      'Covered Firewood Rack',
    ],
    cover: cover(`${IMG}1792696837991_landing-cover-volume-06-a1c4db34.webp`, '06', 'Patio, Garden & Outdoor'),
  },
]

const BONUS_COVER_SIZE = { width: 1102, height: 1427 }

const bonus = (number: string, title: string, benefit: string, src: string): Bonus => ({
  number,
  title,
  benefit,
  cover: { src, alt: `Bonus ${number} cover: ${title} — Woodworking Furniture Plans™`, ...BONUS_COVER_SIZE },
})

export const BONUSES: Bonus[] = [
  bonus(
    '01',
    'Material Purchase & Project Planner',
    'Organize your material purchases and project notes.',
    `${IMG}1792696846438_landing-bonus-01-67b364f5.webp`,
  ),
  bonus(
    '02',
    'Cut Layout & Part Labeling Kit',
    'Plan your cutting layout and identify parts before assembly.',
    `${IMG}1792696852067_landing-bonus-02-a0b51e94.webp`,
  ),
  bonus(
    '03',
    'Joinery & Hardware Setup Reference',
    'Keep joinery and hardware setup information close at hand.',
    `${IMG}1792696858152_landing-bonus-03-11412880.webp`,
  ),
  bonus(
    '04',
    'Surface Prep & Finishing Planner',
    'Organize surface preparation and finishing steps.',
    `${IMG}1792696864032_landing-bonus-04-c9f16445.webp`,
  ),
  bonus(
    '05',
    'Furniture Costing & Quote Workbook',
    'Track project costs and prepare furniture quotes.',
    `${IMG}1792696869790_landing-bonus-05-40c78820.webp`,
  ),
]

export const OFFER_FEATURES: string[] = [
  'Dimensions and technical views in inches and millimeters',
  'Material lists and finished cut sizes',
  'Exploded views, joinery, and hardware details',
  'Illustrated assembly guidance',
  'Commercial build rights included',
  '5 bonus resources included free',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of 60 furniture projects organized into six PDF volumes, with dimensions, materials, cut lists, technical illustrations, and assembly guidance.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'Woodworkers, furniture makers, and DIY builders with basic woodworking skills who want plans for furniture they can build for themselves or sell.',
  },
  {
    question: 'Is this a beginner woodworking course?',
    answer:
      'This collection focuses on furniture projects and build information. Familiarity with woodworking tools and basic assembly techniques will help you use the plans.',
  },
  {
    question: 'Are dimensions included in inches and millimeters?',
    answer: 'Yes. The project pages include dimensions in both inches and millimeters.',
  },
  {
    question: 'Are these physical books or furniture?',
    answer:
      'You are purchasing digital PDF files. Physical books, furniture, tools, and materials are not included.',
  },
  {
    question: 'How will I receive my files?',
    answer:
      'After payment, you’ll receive an email with access to all six PDF volumes and the five bonus resources.',
  },
  {
    question: 'Can I view the files on a tablet or computer?',
    answer:
      'Yes. You can view the PDFs on a tablet or computer, or print the pages you want to use in your workshop.',
  },
  {
    question: 'Can I preview a complete project?',
    answer:
      'Yes. The preview section shows all eight pages of the Three-Drawer Document Cabinet project from Volume 01.',
  },
  {
    question: 'Can I sell the furniture I build?',
    answer:
      'Yes. You may build and sell the physical furniture made from these designs. The digital plans and illustrations may not be resold, shared, or redistributed.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
